import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';
import styled from 'styled-components';
import {
  DEMAND_TYPE_FILTER_OPTIONS,
  getDemandTypeLabel,
} from '../../services/demandTypeService';
import ChartCard, { ChartLegend, ChartTooltip, TruncatedTick } from './ChartCard';
import {
  ANIMATION,
  AXIS_TICK,
  MOBILE_MQ,
  axisProps,
  demandTypeColor,
  formatNumber,
  gridProps,
  toCount,
  useChartAnimation,
  useMediaQuery,
} from './chartTheme';

const ChartScroll = styled.div`
  width: 100%;
  max-height: 640px;
  overflow: auto;
`;

const ROW_HEIGHT = 44;

const ALL_DEMAND_TYPE_KEYS = DEMAND_TYPE_FILTER_OPTIONS.map((o) => o.value);

const pivotBreakdownRows = (rows) => {
  const byName = new Map();
  for (const row of rows) {
    const name = row?.responsibleUserName;
    const type = String(row?.demandType || 'SUPPORT').toUpperCase();
    const value = toCount(row?.count);
    if (!name) continue;

    if (!byName.has(name)) {
      byName.set(name, { responsibleUserName: name, total: 0 });
    }
    const bucket = byName.get(name);
    bucket[type] = (bucket[type] ?? 0) + value;
    bucket.total += value;
  }

  return [...byName.values()].sort((a, b) => b.total - a.total);
};

const ResolvedClosedByResponsibleChart = ({
  data = [],
  loading = false,
  error = false,
  onRetry = null,
  periodLabel = null,
  demandTypeFilter = [],
}) => {
  const isMobile = useMediaQuery(MOBILE_MQ);
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const seriesKeys = useMemo(() => {
    if (Array.isArray(demandTypeFilter) && demandTypeFilter.length > 0) {
      return demandTypeFilter.map((t) => String(t).toUpperCase());
    }
    const fromData = new Set();
    (Array.isArray(data) ? data : []).forEach((row) => {
      if (row?.demandType) fromData.add(String(row.demandType).toUpperCase());
    });
    if (fromData.size > 0) {
      return ALL_DEMAND_TYPE_KEYS.filter((key) => fromData.has(key));
    }
    return ALL_DEMAND_TYPE_KEYS;
  }, [data, demandTypeFilter]);

  const chartData = useMemo(
    () => pivotBreakdownRows(Array.isArray(data) ? data : []),
    [data]
  );

  const total = useMemo(() => chartData.reduce((sum, row) => sum + row.total, 0), [chartData]);

  const legendItems = seriesKeys.map((key, index) => ({
    key,
    label: getDemandTypeLabel(key),
    color: demandTypeColor(key, index),
  }));

  const chartHeight = Math.max(240, chartData.length * ROW_HEIGHT + 32);

  return (
    <ChartCard
      title="Tickets resolvidos e fechados por responsável"
      description={[periodLabel ? `Período: ${periodLabel}` : null, 'Ordenado pelo total']}
      stat={total}
      statLabel="resolvidos/fechados"
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={chartData.length === 0}
      emptyTitle="Nenhum ticket resolvido no período"
      skeleton="bars"
    >
      {seriesKeys.length > 1 ? (
        <ChartLegend
          items={legendItems}
          activeKey={activeKey}
          onActiveChange={setActiveKey}
          label="Tipos de demanda"
        />
      ) : null}
      <ChartScroll>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 0, right: 44, left: 0, bottom: 0 }}
            barCategoryGap="28%"
          >
            <CartesianGrid {...gridProps} horizontal={false} />
            <XAxis type="number" allowDecimals={false} domain={[0, 'auto']} {...axisProps} />
            <YAxis
              type="category"
              dataKey="responsibleUserName"
              width={isMobile ? 112 : 176}
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={<TruncatedTick maxChars={isMobile ? 14 : 24} />}
            />
            <Tooltip
              cursor={{ fill: 'var(--chart-cursor)' }}
              content={<ChartTooltip nameFormatter={getDemandTypeLabel} showTotal hideZero />}
            />
            {seriesKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                stackId="demandType"
                fill={demandTypeColor(key, index)}
                fillOpacity={activeKey && activeKey !== key ? 0.25 : 1}
                radius={index === seriesKeys.length - 1 ? [0, 4, 4, 0] : 0}
                maxBarSize={20}
                isAnimationActive={animate}
                {...ANIMATION}
              >
                {index === seriesKeys.length - 1 ? (
                  <LabelList
                    dataKey="total"
                    position="right"
                    offset={8}
                    formatter={formatNumber}
                    style={{ ...AXIS_TICK, fill: 'var(--color-text-secondary)', fontWeight: 600 }}
                  />
                ) : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartScroll>
    </ChartCard>
  );
};

ResolvedClosedByResponsibleChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      responsibleUserName: PropTypes.string,
      demandType: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  periodLabel: PropTypes.string,
  demandTypeFilter: PropTypes.arrayOf(PropTypes.string),
};

export default ResolvedClosedByResponsibleChart;
